import { useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import Button from 'components/Button/Button';
import Checkbox from 'components/Checkbox/Checkbox';
import EmptyList from 'components/EmptyList/EmptyList';
import Loader from 'components/Loader/Loader';
import Modal from 'components/Modal/Modal';
import Table from 'components/Table/Table';
import TableBody from 'components/Table/TableBody';
import TableCell from 'components/Table/TableCell';
import TableHead from 'components/Table/TableHead';
import TableRow from 'components/Table/TableRow';
import { MATERIALITY_ASSESSMENT_SURVEY_HEAD_CELLS_MODAL } from 'constants/dataSets';
import { BUTTON_VARIANT } from 'constants/interfaces';
import { getTextDate } from 'utils/formattedData';

import type IQuestionsItem from 'store/slices/survey/interfaces/IQuestionsItem';
import type { ISendSurveyModal } from './interfaces/ISendSurveyModal';

import styles from './scss/SendSurveyModal.module.scss';

function SendSurveyModal({
  open, handleClose, title, handleShare, isLoading, disabledConfirm,
  items, loadMoreQuestions, hasMore, selectedItems, handleSelectItem,
}: ISendSurveyModal) {
  const { t } = useTranslation();
  const tableRef = useRef<HTMLDivElement>(null);

  const isAddDisabled = disabledConfirm || selectedItems.length === 0;

  const emptyListText = (
    <Trans
      i18nKey="common.noResultsWithFilters"
      t={t}
      components={{
        br: <br />,
      }}
    />
  );

  return (
    <Modal
      open={open}
      title={title}
      closeTitle={t('common.cancel')}
      confirmTitle={t('common.shareModal.share')}
      isLoading={isLoading}
      className={styles.modal}
      isButtonsVisible={false}
    >
      <div className={styles.root}>
        <Loader isPageLoading={isLoading} />
        {(items.length > 0 && MATERIALITY_ASSESSMENT_SURVEY_HEAD_CELLS_MODAL) ? (
          <InfiniteScroll
            className={styles.infiniteScroll}
            dataLength={items?.length || 0}
            next={loadMoreQuestions}
            hasMore={hasMore}
            loader={false}
          >
            <div ref={tableRef} className={styles.tableBodyScroll}>
              <Table className={styles.table}>
                <TableHead className={styles.header}>
                  {MATERIALITY_ASSESSMENT_SURVEY_HEAD_CELLS_MODAL.map((col) => (
                    <TableCell head className={styles.tableCell} key={col.id}>
                      <div>
                        <span>{t(col?.label || '')}</span>
                      </div>
                    </TableCell>
                  ))}
                </TableHead>
                <TableBody className={styles.body}>
                  {!isLoading && items?.map((item: IQuestionsItem) => (
                    <TableRow key={[item.id, item.name].join('_')}>
                      <TableCell>
                        <Checkbox
                          name={`survey-${item.id}`}
                          checked={selectedItems.includes(Number(item.id))}
                          value={String(selectedItems.includes(Number(item.id)))}
                          onChange={() => handleSelectItem(Number(item.id))}
                          aria-label={t(item?.name || '')}
                          label={t(item?.name || '')}
                        />
                      </TableCell>
                      <TableCell>
                        <span className={styles.description}>{item?.description}</span>
                      </TableCell>
                      <TableCell>
                        <div className={styles.createdCell}>
                          <span className={styles.titleCreated}>{getTextDate(item?.updatedAt)}</span>
                          {item?.updatedBy?.firstName ? (
                            <span className={styles.nameCreated}>
                              {`by ${item.updatedBy.firstName} ${item.updatedBy.lastName}`}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className={styles.createdCell}>
                          <span className={styles.titleCreated}>{getTextDate(item?.updatedAt)}</span>
                          {item?.updatedBy?.firstName ? (
                            <span className={styles.nameCreated}>
                              {`by ${item.updatedBy.firstName} ${item.updatedBy.lastName}`}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={styles.question}>{item?.questionsCount}</span>
                      </TableCell>
                      <TableCell>
                        <div className={styles.statusCell}>
                          <span className={`${styles.statusText} ${styles[item?.status]}`}>
                            <span>{t(`materialityAssessment.statusOptions.${item?.status}`)}</span>
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </InfiniteScroll>
        ) : <EmptyList text={emptyListText} />}
        <div className={styles.buttonWrapper}>
          <Button
            title={t('common.cancel')}
            variant={BUTTON_VARIANT.cancel}
            className={styles.button}
            onClick={handleClose}
          />
          <Button
            title={t('common.add')}
            onClick={handleShare}
            className={styles.button}
            disabled={isAddDisabled}
          />
        </div>
      </div>
    </Modal>
  );
}

export default SendSurveyModal;
